// AdminLeaseToOwnDocuments.js
import React, { useState, useEffect } from 'react';
import { storage } from './firebase';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';

// Proxy-relative in dev, full URL in production
const LEASE_DOCS_API = process.env.NODE_ENV === 'production'
  ? `${process.env.REACT_APP_API_URL}/api/lease-to-own-documents`  // ← your real backend
  : '/api/lease-to-own-documents';

function AdminLeaseToOwnDocuments() {
  const [currentDocument, setCurrentDocument] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null); // Added missing error state
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState('');
  
  // Note states
  const [noteTitle, setNoteTitle] = useState('');
  const [noteBody, setNoteBody] = useState('');
  const [orderNumber, setOrderNumber] = useState(1);
  const [editingNote, setEditingNote] = useState(null);
  const [savingNote, setSavingNote] = useState(false);

  const fetchDocument = async () => {
    setLoading(true);
    setError(null);

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);

    try {
      const res = await fetch(LEASE_DOCS_API, {
        signal: controller.signal,
        headers: {
          'Accept': 'application/json',
          'X-API-Key': process.env.REACT_APP_VEHICLES_API_KEY || '',
        },
        credentials: 'same-origin',
      });

      clearTimeout(timeoutId);

      if (!res.ok) {
        if (res.status === 401) throw new Error('Unauthorized - invalid or missing API key');
        throw new Error(`HTTP error! Status: ${res.status}`);
      }

      const data = await res.json();
      if (data.success && data.data) {
        setCurrentDocument(data.data);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (err) {
      console.error('Fetch failed:', err);
      setError(err.message || 'Failed to load lease-to-own document');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDocument();
  }, []);

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ];
    if (!allowedTypes.includes(file.type)) {
      setMessage('❌ Only PDF, DOC, and DOCX files are allowed');
      return;
    }

    setUploading(true);
    setMessage('');

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000); // longer for upload

    try {
      const fileRef = ref(storage, `lease-to-own-documents/${Date.now()}_${file.name}`);
      await uploadBytes(fileRef, file);
      const downloadURL = await getDownloadURL(fileRef);

      const payload = {
        fileName: file.name,
        fileURL: downloadURL,
        fileType: file.type
      };

      const res = await fetch(LEASE_DOCS_API, {
        method: currentDocument ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': process.env.REACT_APP_VEHICLES_API_KEY || '',
        },
        body: JSON.stringify(payload),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to save document');
      }

      setMessage('✅ Document uploaded successfully!');
      fetchDocument();
    } catch (err) {
      console.error('Upload failed:', err);
      setMessage('❌ Upload failed: ' + (err.message || 'Unknown error'));
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete the current document?')) return;

    setUploading(true);
    setMessage('');

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);

    try {
      if (currentDocument?.fileURL) {
        await deleteObject(ref(storage, currentDocument.fileURL)).catch(err => {
          if (err.code !== 'storage/object-not-found') throw err;
        });
      }

      const res = await fetch(LEASE_DOCS_API, {
        method: 'DELETE',
        headers: {
          'X-API-Key': process.env.REACT_APP_VEHICLES_API_KEY || '',
        },
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!res.ok) throw new Error('Failed to delete document');

      setCurrentDocument(null);
      setMessage('✅ Document deleted successfully');
    } catch (err) {
      console.error('Delete failed:', err);
      setMessage('❌ Failed to delete document: ' + (err.message || 'Unknown error'));
    } finally {
      setUploading(false);
    }
  };

  const handleSaveNote = async (e) => {
    e.preventDefault();
    if (!noteTitle.trim() || !noteBody.trim()) {
      setMessage('❌ Please fill in both note title and body');
      return;
    }

    setSavingNote(true);
    setMessage('');

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);

    try {
      const url = editingNote 
        ? `${LEASE_DOCS_API}/notes/${editingNote._id}`
        : `${LEASE_DOCS_API}/notes`;
      
      const method = editingNote ? 'PUT' : 'POST';
      
      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': process.env.REACT_APP_VEHICLES_API_KEY || '',
        },
        body: JSON.stringify({ noteTitle, noteBody, orderNumber }),
        signal: controller.signal,
      });
      
      clearTimeout(timeoutId);

      const data = await res.json();
      
      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Failed to save note');
      }
      
      setCurrentDocument(data.data);
      setNoteTitle('');
      setNoteBody('');
      setOrderNumber(data.data.notes.length + 1);
      setEditingNote(null);
      setMessage(editingNote ? '✅ Note updated successfully!' : '✅ Note added successfully!');
    } catch (err) {
      console.error('Note save failed:', err);
      setMessage('❌ ' + (err.message || 'Failed to save note'));
    } finally {
      setSavingNote(false);
    }
  };

  const handleEditNote = (note) => {
    setNoteTitle(note.noteTitle);
    setNoteBody(note.noteBody);
    setOrderNumber(note.orderNumber);
    setEditingNote(note);
    // Optional: scroll to form
    window.scrollTo({ top: document.getElementById('note-form')?.offsetTop - 100, behavior: 'smooth' });
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Are you sure you want to delete this note?')) return;

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);

    try {
      const res = await fetch(`${LEASE_DOCS_API}/notes/${noteId}`, {
        method: 'DELETE',
        headers: {
          'X-API-Key': process.env.REACT_APP_VEHICLES_API_KEY || '',
        },
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      const data = await res.json();
      
      if (!res.ok || !data.success) {
        throw new Error('Failed to delete note');
      }
      
      setCurrentDocument(data.data);
      setMessage('✅ Note deleted successfully!');
    } catch (err) {
      console.error('Note delete failed:', err);
      setMessage('❌ Failed to delete note: ' + (err.message || 'Unknown error'));
    }
  };

  const cancelEditNote = () => {
    setNoteTitle('');
    setNoteBody('');
    setOrderNumber(currentDocument?.notes?.length + 1 || 1);
    setEditingNote(null);
  };

  const getFileIcon = (fileName) => {
    const ext = fileName?.split('.').pop()?.toLowerCase();
    switch (ext) {
      case 'pdf': return '📕';
      case 'doc':
      case 'docx': return '📘';
      default: return '📄';
    }
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#f8f9fa',
        fontSize: '1.8rem',
        color: '#333'
      }}>
        Loading lease-to-own content...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#f8f9fa',
        color: '#dc3545',
        textAlign: 'center',
        padding: '2rem'
      }}>
        <h2 style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>Error Loading Content</h2>
        <p style={{ fontSize: '1.3rem', maxWidth: '600px' }}>{error}</p>
        <button
          onClick={fetchDocument}
          style={{
            marginTop: '2rem',
            padding: '1rem 2.5rem',
            background: '#2c5282',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '1.1rem',
            cursor: 'pointer'
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: '#f8f9fa', 
      padding: 'clamp(1rem, 3vw, 2rem)'
    }}>
      <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: 'clamp(2rem, 4vw, 3rem)', textAlign: 'center' }}>
          <h1 style={{ 
            fontSize: 'clamp(1.8rem, 4vw, 2.5rem)', 
            fontWeight: '300', 
            color: '#222', 
            marginBottom: '0.5rem' 
          }}>
            Lease-to-Own <span style={{ fontWeight: '600', color: '#2c5282' }}>Agreement</span>
          </h1>
          <p style={{ color: '#666', fontSize: 'clamp(0.9rem, 2vw, 1rem)' }}>
            Manage the lease-to-own agreement document and important notes
          </p>
        </div>

        {/* Message */}
        {message && (
          <div style={{
            padding: 'clamp(0.8rem, 2vw, 1rem)',
            marginBottom: 'clamp(1rem, 2vw, 2rem)',
            borderRadius: '8px',
            background: message.startsWith('✅') ? '#d4edda' : '#f8d7da',
            color: message.startsWith('✅') ? '#155724' : '#721c24',
            textAlign: 'center',
            fontSize: 'clamp(0.9rem, 2vw, 1rem)'
          }}>
            {message}
          </div>
        )}

        {/* Current Document Card */}
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: 'clamp(1.5rem, 3vw, 2rem)',
          boxShadow: '0 8px 25px rgba(0,0,0,0.08)',
          marginBottom: 'clamp(1.5rem, 3vw, 2rem)'
        }}>
          <h2 style={{ 
            fontSize: 'clamp(1.2rem, 3vw, 1.5rem)', 
            fontWeight: '600', 
            marginBottom: '1.5rem', 
            color: '#333' 
          }}>
            📄 Current Agreement Document
          </h2>

          {currentDocument ? (
            <div>
              <div style={{
                padding: 'clamp(1rem, 2vw, 1.5rem)',
                background: '#f8f9fa',
                borderRadius: '12px',
                border: '1px solid #e0e0e0',
                marginBottom: '1.5rem'
              }}>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 'clamp(0.8rem, 2vw, 1rem)',
                  flexWrap: 'wrap'
                }}>
                  <div style={{ fontSize: 'clamp(2.5rem, 5vw, 3.5rem)' }}>
                    {getFileIcon(currentDocument.fileName)}
                  </div>
                  <div style={{ flex: '1 1 200px', minWidth: 0 }}>
                    <div style={{ 
                      fontSize: 'clamp(1rem, 2vw, 1.2rem)', 
                      fontWeight: '600', 
                      color: '#333', 
                      marginBottom: '0.3rem',
                      wordBreak: 'break-word'
                    }}>
                      {currentDocument.fileName}
                    </div>
                    <div style={{ fontSize: 'clamp(0.85rem, 1.5vw, 0.95rem)', color: '#666' }}>
                      Uploaded on {new Date(currentDocument.updatedAt || currentDocument.createdAt).toLocaleDateString()}
                    </div>
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                <a
                  href={currentDocument.fileURL}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    flex: '1 1 200px',
                    padding: 'clamp(0.8rem, 2vw, 1rem)',
                    background: '#2c5282',
                    color: 'white',
                    textAlign: 'center',
                    borderRadius: '8px',
                    textDecoration: 'none',
                    fontWeight: '600'
                  }}
                >
                  View Document
                </a>
                <button
                  onClick={handleDelete}
                  disabled={uploading}
                  style={{
                    flex: '1 1 200px',
                    padding: 'clamp(0.8rem, 2vw, 1rem)',
                    background: uploading ? '#ccc' : '#e53e3e',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: '600',
                    cursor: uploading ? 'not-allowed' : 'pointer'
                  }}
                >
                  {uploading ? 'Processing...' : 'Delete Document'}
                </button>
              </div>
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: 'clamp(2rem, 4vw, 3rem)', color: '#888' }}>
              <div style={{ fontSize: 'clamp(3rem, 6vw, 4rem)', marginBottom: '1rem' }}>📄</div>
              <p style={{ fontSize: 'clamp(1rem, 2vw, 1.2rem)' }}>No lease-to-own agreement document uploaded yet</p>
            </div>
          )}
        </div>

        {/* Upload Section */}
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: 'clamp(1.5rem, 3vw, 2rem)',
          boxShadow: '0 8px 25px rgba(0,0,0,0.08)',
          marginBottom: 'clamp(1.5rem, 3vw, 2rem)'
        }}>
          <h2 style={{ 
            fontSize: 'clamp(1.2rem, 3vw, 1.5rem)', 
            fontWeight: '600', 
            marginBottom: '1rem', 
            color: '#333' 
          }}>
            {currentDocument ? '📤 Replace Document' : '📤 Upload Lease-to-Own Document'}
          </h2>
          <p style={{ 
            color: '#666', 
            marginBottom: '1.5rem', 
            fontSize: 'clamp(0.85rem, 2vw, 0.95rem)' 
          }}>
            Supported formats: PDF, DOC, DOCX (Max recommended size: 10MB)
          </p>

          <label style={{
            display: 'block',
            width: '100%',
            padding: 'clamp(1.5rem, 3vw, 2rem)',
            background: uploading ? '#f0f0f0' : '#f8f9fa',
            border: '2px dashed #2c5282',
            borderRadius: '12px',
            textAlign: 'center',
            cursor: uploading ? 'not-allowed' : 'pointer',
            transition: 'all 0.3s',
            opacity: uploading ? 0.6 : 1
          }}>
            <input
              type="file"
              accept=".pdf,.doc,.docx"
              onChange={handleFileUpload}
              disabled={uploading}
              style={{ display: 'none' }}
            />
            <div style={{ fontSize: 'clamp(2.5rem, 5vw, 3.5rem)', marginBottom: '1rem' }}>
              {uploading ? '⏳' : '📁'}
            </div>
            <div style={{ 
              fontSize: 'clamp(1rem, 2vw, 1.2rem)', 
              fontWeight: '600', 
              color: '#2c5282', 
              marginBottom: '0.5rem' 
            }}>
              {uploading ? 'Uploading...' : 'Click or drag to upload document'}
            </div>
            <div style={{ fontSize: 'clamp(0.85rem, 1.5vw, 0.95rem)', color: '#666' }}>
              PDF, DOC, DOCX only
            </div>
          </label>
        </div>

        {/* Notes Management Section */}
        <div id="note-form" style={{
          background: 'white',
          borderRadius: '16px',
          padding: 'clamp(1.5rem, 3vw, 2rem)',
          boxShadow: '0 8px 25px rgba(0,0,0,0.08)',
          marginBottom: 'clamp(1.5rem, 3vw, 2rem)'
        }}>
          <h2 style={{ 
            fontSize: 'clamp(1.2rem, 3vw, 1.5rem)', 
            fontWeight: '600', 
            marginBottom: '1rem', 
            color: '#333' 
          }}>
            📝 {editingNote ? 'Edit Note' : 'Add Important Note'}
          </h2>
          <p style={{ 
            color: '#666', 
            marginBottom: '1.5rem', 
            fontSize: 'clamp(0.85rem, 2vw, 0.95rem)' 
          }}>
            {editingNote ? 'Update the note details' : 'Add key terms, conditions or information for customers'}
          </p>

          <form onSubmit={handleSaveNote}>
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '0.5rem', 
                fontWeight: '600', 
                color: '#333',
                fontSize: 'clamp(0.9rem, 2vw, 1rem)'
              }}>
                Order Number
              </label>
              <input
                type="number"
                value={orderNumber}
                onChange={(e) => setOrderNumber(parseInt(e.target.value) || 1)}
                min="1"
                style={{
                  width: 'clamp(80px, 20vw, 150px)',
                  padding: 'clamp(0.7rem, 2vw, 1rem)',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  fontSize: 'clamp(0.9rem, 2vw, 1rem)',
                  outline: 'none'
                }}
              />
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '0.5rem', 
                fontWeight: '600', 
                color: '#333',
                fontSize: 'clamp(0.9rem, 2vw, 1rem)'
              }}>
                Note Title
              </label>
              <input
                type="text"
                value={noteTitle}
                onChange={(e) => setNoteTitle(e.target.value)}
                placeholder="e.g. Important Payment Terms"
                style={{
                  width: '100%',
                  padding: 'clamp(0.7rem, 2vw, 1rem)',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  fontSize: 'clamp(0.9rem, 2vw, 1rem)',
                  outline: 'none'
                }}
              />
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '0.5rem', 
                fontWeight: '600', 
                color: '#333',
                fontSize: 'clamp(0.9rem, 2vw, 1rem)'
              }}>
                Note Content
              </label>
              <textarea
                value={noteBody}
                onChange={(e) => setNoteBody(e.target.value)}
                placeholder="Detailed explanation of the term or condition..."
                rows={5}
                style={{
                  width: '100%',
                  padding: 'clamp(0.7rem, 2vw, 1rem)',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  fontSize: 'clamp(0.9rem, 2vw, 1rem)',
                  fontFamily: 'inherit',
                  resize: 'vertical',
                  outline: 'none'
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
              <button
                type="submit"
                disabled={savingNote}
                style={{
                  flex: '1 1 200px',
                  padding: 'clamp(0.8rem, 2vw, 1rem)',
                  background: savingNote ? '#888' : '#2c5282',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: '600',
                  cursor: savingNote ? 'not-allowed' : 'pointer',
                  fontSize: 'clamp(0.9rem, 2vw, 1rem)'
                }}
              >
                {savingNote ? 'Saving...' : (editingNote ? 'Update Note' : 'Add Note')}
              </button>

              {editingNote && (
                <button
                  type="button"
                  onClick={cancelEditNote}
                  style={{
                    flex: '1 1 200px',
                    padding: 'clamp(0.8rem, 2vw, 1rem)',
                    background: '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    fontSize: 'clamp(0.9rem, 2vw, 1rem)'
                  }}
                >
                  Cancel Edit
                </button>
              )}
            </div>
          </form>
        </div>

        {/* Existing Notes List */}
        {currentDocument?.notes && currentDocument.notes.length > 0 && (
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: 'clamp(1.5rem, 3vw, 2rem)',
            boxShadow: '0 8px 25px rgba(0,0,0,0.08)'
          }}>
            <h2 style={{ 
              fontSize: 'clamp(1.2rem, 3vw, 1.5rem)', 
              fontWeight: '600', 
              marginBottom: '1.5rem', 
              color: '#333' 
            }}>
              📋 Current Notes ({currentDocument.notes.length})
            </h2>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 'clamp(1rem, 2vw, 1.5rem)' }}>
              {currentDocument.notes.sort((a, b) => a.orderNumber - b.orderNumber).map((note) => (
                <div
                  key={note._id}
                  style={{
                    background: '#f8f9fa',
                    border: '1px solid #e0e0e0',
                    borderLeft: '4px solid #2c5282',
                    padding: 'clamp(1rem, 2vw, 1.5rem)',
                    borderRadius: '12px'
                  }}
                >
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'start',
                    gap: '1rem',
                    flexWrap: 'wrap'
                  }}>
                    <div style={{ flex: '1 1 300px', minWidth: 0 }}>
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 'clamp(0.5rem, 2vw, 1rem)', 
                        marginBottom: '0.5rem',
                        flexWrap: 'wrap'
                      }}>
                        <span style={{
                          background: '#2c5282',
                          color: 'white',
                          padding: '0.25rem 0.75rem',
                          borderRadius: '20px',
                          fontSize: 'clamp(0.8rem, 1.5vw, 0.9rem)',
                          fontWeight: '600'
                        }}>
                          #{note.orderNumber}
                        </span>
                        <h3 style={{ 
                          fontSize: 'clamp(1.05rem, 2vw, 1.2rem)', 
                          color: '#333', 
                          margin: 0,
                          fontWeight: '600',
                          wordBreak: 'break-word'
                        }}>
                          {note.noteTitle}
                        </h3>
                      </div>
                      <p style={{ 
                        color: '#555', 
                        lineHeight: '1.7', 
                        margin: '0.5rem 0 0 0',
                        fontSize: 'clamp(0.9rem, 2vw, 1rem)',
                        wordBreak: 'break-word',
                        whiteSpace: 'pre-wrap'
                      }}>
                        {note.noteBody}
                      </p>
                    </div>

                    <div style={{ 
                      display: 'flex', 
                      gap: 'clamp(0.5rem, 1vw, 0.75rem)',
                      flexShrink: 0
                    }}>
                      <button
                        onClick={() => handleEditNote(note)}
                        style={{
                          padding: 'clamp(0.5rem, 1.5vw, 0.7rem) clamp(0.8rem, 2vw, 1.2rem)',
                          background: '#4A90E2',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: 'clamp(0.85rem, 1.5vw, 0.95rem)',
                          fontWeight: '600',
                          cursor: 'pointer'
                        }}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteNote(note._id)}
                        style={{
                          padding: 'clamp(0.5rem, 1.5vw, 0.7rem) clamp(0.8rem, 2vw, 1.2rem)',
                          background: '#e53e3e',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: 'clamp(0.85rem, 1.5vw, 0.95rem)',
                          fontWeight: '600',
                          cursor: 'pointer'
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminLeaseToOwnDocuments;